import type { ReactNode } from 'react';
import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { env } from '@/lib/env';

/**
 * Auth0ProviderWithNavigate — mounts the Auth0 SDK inside BrowserRouter so
 * it can drive post-login navigation via `onRedirectCallback`.
 *
 * Without this wrapper, Auth0 redirects back to
 * `http://localhost:5173/?code=…&state=…` and React Router's top-level
 * `<Navigate to="/library" replace />` fires on the first render — stripping
 * the `code`/`state` query params *before* the SDK gets a chance to
 * exchange them for an access token. Letting Auth0 own the navigation via
 * `onRedirectCallback` guarantees the code exchange completes first.
 *
 * `appState.returnTo` is the path the user was trying to reach before
 * being bounced to Auth0 (set from the Login page); we fall back to
 * `/library` for a fresh sign-in.
 */
export function Auth0ProviderWithNavigate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  if (!env.isAuth0Configured) {
    return <>{children}</>;
  }

  const onRedirectCallback = (appState?: AppState) => {
    const target = appState?.returnTo ?? '/library';
    navigate(target, { replace: true });
  };

  return (
    <Auth0Provider
      domain={env.auth0Domain}
      clientId={env.auth0ClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: env.auth0Audience,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
